import {DomainGraphType} from 'app-core/framework/CoreObjectConstants';
import {getApiBaseForObjectType} from 'app-core/framework/CoreObjectIntrospectionUtils';
import BenchlingConfig from 'BenchlingConfig';
import {
  AnalysisInputs,
  MethodAnalysisSources,
  WorksheetAnalysisSources,
} from 'procedures/design/ProcedureMethodAnalysisTypes';
import {getJSONPromise} from 'util/Net';
import * as TaskRunner from 'util/TaskRunner';

/**
 * Backfills fields the server may not have sent yet -- getJSONPromise doesn't validate the response
 * against our types, so a server predating a given field (e.g. mid-deploy, running old code) sends a
 * response simply missing it rather than an empty array/null, unlike our types which mark it required.
 */
function withAnalysisInputsDefaults<T extends {analysisInputs: AnalysisInputs}>(
  sourcesPerStep: Array<T>
): Array<T> {
  return sourcesPerStep.map((source) => ({
    ...source,
    analysisInputs: {
      ...source.analysisInputs,
      scratchPlates: source.analysisInputs.scratchPlates ?? [],
    },
  }));
}

function withWorksheetAnalysisSourcesDefaults(result: WorksheetAnalysisSources): WorksheetAnalysisSources {
  return {
    ...result,
    sourcesPerStep: withAnalysisInputsDefaults(result.sourcesPerStep),
  };
}

export async function fetchWorksheetAnalysisSources(worksheetId: string): Promise<WorksheetAnalysisSources> {
  const worksheetUrl = `${getApiBaseForObjectType(DomainGraphType.PROCEDURE)}/${worksheetId}`;

  if (!BenchlingConfig.STUDIES_ENABLE_ASYNC_WORKSHEET_ANALYSIS_SOURCES) {
    const result: WorksheetAnalysisSources = await getJSONPromise(`${worksheetUrl}/analysis-sources`);
    return withWorksheetAnalysisSourcesDefaults(result);
  }

  const result: WorksheetAnalysisSources = await TaskRunner.postJSONPromise({
    url: `${worksheetUrl}/analysis-sources-task`,
  });
  return withWorksheetAnalysisSourcesDefaults(result);
}

export async function fetchProcedureMethodDefinitionAnalysisSources(
  pmdvId: string
): Promise<MethodAnalysisSources> {
  const result: MethodAnalysisSources = await getJSONPromise(
    `${getApiBaseForObjectType(DomainGraphType.PROCEDURE)}/${pmdvId}/method-definition-analysis-sources`
  );
  return {
    ...result,
    sourcesPerStep: withAnalysisInputsDefaults(result.sourcesPerStep),
  };
}
